
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Play, Pause, RefreshCw, Timer as TimerIcon } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from '@/components/ui/use-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface TimerProps {
  taskId: string;
}

const Timer = ({ taskId }: TimerProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [timeLeft, setTimeLeft] = useState(25 * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState('25');
  const [startTime, setStartTime] = useState<Date | null>(null);

  const durationOptions = [
    { value: '15', label: '15 minutes' },
    { value: '25', label: '25 minutes' },
    { value: '45', label: '45 minutes' },
  ];

  // Cargar la configuración de sonido
  const notificationSound = new Audio('/notification.mp3');

  const { mutate: saveTimeEntry } = useMutation({
    mutationFn: async (duration: number) => {
      if (!startTime) return;
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) throw new Error('No authenticated user');

      const { error } = await supabase
        .from('time_entries')
        .insert({
          task_id: taskId,
          user_id: user.id,
          started_at: startTime.toISOString(),
          ended_at: new Date().toISOString(),
          duration: duration,
          type: 'pomodoro'
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todays-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    }
  });

  const onTimerComplete = useCallback(() => {
    setIsRunning(false);
    notificationSound.play();
    toast({
      title: "Pomodoro completed!",
      description: "Time to take a break.",
    });

    if (startTime) {
      const duration = (parseInt(selectedDuration) * 60) - timeLeft;
      saveTimeEntry(duration);
    }
  }, [selectedDuration, timeLeft, startTime, toast, saveTimeEntry]);

  useEffect(() => {
    let interval: number | undefined;

    if (isRunning && timeLeft > 0) {
      interval = window.setInterval(() => {
        setTimeLeft((time) => {
          if (time <= 1) {
            onTimerComplete();
            return 0;
          }
          return time - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, timeLeft, onTimerComplete]);

  const toggleTimer = () => {
    if (!isRunning && timeLeft === parseInt(selectedDuration) * 60) {
      setStartTime(new Date());
    }
    setIsRunning(!isRunning);
  };

  const resetTimer = () => {
    setIsRunning(false);
    setTimeLeft(parseInt(selectedDuration) * 60);
    setStartTime(null);
  };

  const handleDurationChange = (value: string) => {
    setSelectedDuration(value);
    setTimeLeft(parseInt(value) * 60);
    setStartTime(null);
  };

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <div className="flex items-center gap-3 bg-card p-3 rounded-lg">
      <div className="flex items-center gap-2">
        <TimerIcon className="h-4 w-4 text-muted-foreground" />
        <Select
          value={selectedDuration}
          onValueChange={handleDurationChange}
          disabled={isRunning}
        >
          <SelectTrigger className="w-[100px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {durationOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <span className="text-2xl font-mono w-20 text-center">
        {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
      </span>
      
      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTimer}
          className="h-8 w-8"
        >
          {isRunning ? <Pause size={16} /> : <Play size={16} />}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={resetTimer}
          className="h-8 w-8"
        >
          <RefreshCw size={16} />
        </Button>
      </div>
    </div>
  );
};

export default Timer;
